/**
 * @file コンポーネント：ノート ロジック管理
 */

// ライブラリ読み込み
import React, { useEffect, useRef, useState } from "react";

// Presenter読み込み
import { Presenter } from "./presenter";

// 関数読み込み
import {
    fnConvertSecondToTimeString,
    fnConvertTimeStringToSecond,
    fnHandleApiErrors,
    fnHandleApiSuccess,
    fnZeroPadding,
} from "~/src/functions/common";
import { fnIsTouchDevice } from "~/src/functions/boolean";

// コンポーネントを返す
export const Note = (props) => {
    // ローカライズデータ
    const localized = window.listLocalized;

    /**
     * 初期設定
     */
    // ツールバーのカラーセット
    const colors = [
        "#000000",
        "#e60000",
        "#ff9900",
        "#ffff00",
        "#008a00",
        "#0066cc",
        "#9933ff",
        "#ffffff",
        "#facccc",
        "#ffebcc",
        "#ffffcc",
        "#cce8cc",
        "#cce0f5",
        "#ebd6ff",
        "#bbbbbb",
        "#f06666",
        "#ffc266",
        "#ffff66",
        "#66b966",
        "#66a3e0",
        "#c285ff",
        "#888888",
        "#a10000",
        "#b26b00",
        "#b2b200",
        "#006100",
        "#0047b2",
        "#6b24b2",
        "#444444",
        "#5c0000",
        "#663d00",
        "#666600",
        "#003700",
        "#002966",
        "#3d1466",
    ];
    // ツールバー設定
    const [modules, setModules] = useState(null); // ツールバー設定
    useEffect(() => {
        setModules({
            toolbar: {
                container: "#js-note-toolbar",
                handlers: {
                    addtimeline: fnOnClickAddTimeline,
                },
            },
        });
    }, []);
    // ノート取得
    const [defaultValue, setDefaultValue] = useState([]); // ノート本文デフォルト値
    const [isLoading, setIsLoading] = useState(true); // ローディング状態
    const refQuill = useRef(null); // ノート要素
    useEffect(() => {
        if (
            (props.noteId !== null && props.noteId !== undefined) ||
            (props.operationId !== null && props.operationId !== undefined)
        ) {
            // ノートの初期化
            setIsLoading(true);
            setDefaultValue([]);
            // ノート取得のAPIリクエスト
            fnGetNote();
        }
    }, [props.noteId]);

    /**
     * ノートにフル動画ジャンプリンクを追加する処理
     */
    const refButtonAddTimeline = useRef(null); // Addtimelineボタン
    const fnOnClickAddTimeline = () => {
        const quill = refQuill.current.editor;
        // 挿入位置
        const index = quill.getSelection().index;
        // 挿入テキスト
        const timeProgress = refButtonAddTimeline.current.dataset.timeProgress;
        const text = "[" + timeProgress + "]";
        // テキストクリック時のリンク先
        const timeProgressSecond = fnConvertTimeStringToSecond(timeProgress);
        const path =
            location.origin +
            location.pathname +
            "?from=" +
            timeProgressSecond +
            "&tab=note";
        // テキストの挿入
        quill.insertText(index, text, "link", path);
        // 挿入後のフォーカス位置
        quill.setSelection(index + text.length);
    };
    // ローディング完了後の処理
    useEffect(() => {
        if (
            isLoading === false &&
            props.isLoadingFullvideo !== true &&
            refQuill !== null &&
            refQuill.current !== null
        ) {
            fnAddClickEvent();
        }
    }, [isLoading, refQuill, props.isLoadingFullvideo]);

    /**
     * ノート内のリンクにクリック時の処理を追加
     */
    const fnAddClickEvent = () => {
        const anchors = refQuill.current.editor.root.querySelectorAll("a");
        anchors?.forEach((anchor) => {
            anchor.onclick = (e) => {
                e.preventDefault();
                if (props.operationId !== undefined) {
                    // フル動画ページの場合、該当時間にジャンプ
                    const anchorHref =
                        anchor.href.indexOf("&tab=note") !== -1
                            ? anchor.href.replace("&tab=note", "")
                            : anchor.href;
                    const second = anchorHref.split("from=", 2)[1];
                    const timeString = fnConvertSecondToTimeString(second);
                    props.fnJump(timeString);
                } else {
                    // マイページの場合、リンク先に遷移
                    location.href = anchor.href;
                }
            };
        });
    };

    /**
     * ノート取得のAPIリクエスト処理
     */
    const [updatetime, setUpdatetime] = useState(""); // 最終更新時間
    // ノート取得
    const fnGetNote = () => {
        let api;
        if (props.noteId !== null && props.noteId !== undefined) {
            // マイページからの参照
            api = "/api/Notes/get?id=" + props.noteId;
        } else if (
            props.operationId !== null &&
            props.operationId !== undefined
        ) {
            // フル動画ページからの参照
            api =
                "/api/Notes/get/operation?operationId=" +
                window.atob(props.operationId);
        }
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    setDefaultValue(JSON.parse(result.context));
                    setUpdatetime(fnConvertToUTCFormat(result.lastDate));
                    // マイページからの参照
                    if (props.noteId !== undefined) {
                        fnNotesActLogs(props.noteId);
                    }
                }
                setIsLoading(false);
            })
            .catch((error) => {
                console.error(error, localized["ErrorMessage.FailGetNote"]);
                setIsLoading(false);
            });
    };
    // ノートアクションログの追加
    const fnNotesActLogs = (noteId) => {
        const api = "/api/NotesActLogs/add";
        const token = document
            .getElementById("requestverificationtoken")
            .getAttribute("value");
        const options = {
            body: JSON.stringify({
                NoteId: noteId,
                PreviousRoute: "mypage",
                Route: "mypage",
                Status: null,
                Url: null,
            }),
            headers: {
                "Content-Type": "application/json",
                RequestVerificationToken: token,
            },
            method: "POST",
        };
        fetch(api, options)
            .then((res) => {
                if (res.url.indexOf(api) === -1) {
                    console.log("Error 302");
                } else if (res.status !== 200) {
                    console.log("Error");
                }
            })
            .catch((error) => {
                console.error(error);
            });
    };

    /**
     * 削除ボタンクリック時の処理
     */
    const [isOpenModalNoteDelete, setIsOpenModalNoteDelete] = useState(false); // ノート削除モーダルの開閉状態
    const fnOnClickDelete = (e) => {
        e.preventDefault();
        setIsOpenModalNoteDelete(true);
    };

    /**
     * 保存ボタンクリック時の処理
     */
    const [textNavigation, setTextNavigation] = useState(""); // ナビゲーションテキスト
    const fnOnClickSave = async (e) => {
        e.preventDefault();
        let api, options;
        const quillContents = refQuill.current.editor.getContents();
        const contents = JSON.stringify(quillContents.ops);
        if (
            props.noteId === undefined &&
            props.operationId !== null &&
            props.operationId !== undefined
        ) {
            // 追加
            api = "/api/Notes/add";
            options = {
                body: JSON.stringify({
                    contents,
                    operationId: window.atob(props.operationId),
                }),
            };
        } else {
            // 更新
            api = "/api/Notes/update";
            options = {
                body: JSON.stringify({
                    contents,
                    noteId: props.noteId,
                }),
            };
        }
        options.headers = {
            "Content-Type": "application/json",
            RequestVerificationToken: document
                .getElementById("requestverificationtoken")
                .getAttribute("value"),
        };
        options.method = "POST";
        fetch(api, options)
            .then(fnHandleApiErrors)
            .then((response) => {
                if (response.status === 200) {
                    setTextNavigation(localized["Message.NoteSaved"]);
                    setTimeout(() => {
                        setTextNavigation("");
                    }, 2000);
                    fnGetNote();
                }
            })
            .catch((error) => {
                console.error(
                    error + " error",
                    localized["ErrorMessage.FailSavedNote"]
                );
            });
    };

    /**
     * 日時表記をUTCフォーマットに変換
     */
    const fnConvertToUTCFormat = (utcDate = null) => {
        const time = utcDate === null ? new Date() : new Date(utcDate);
        const Y = time.getUTCFullYear();
        const M = time.getUTCMonth() + 1;
        const D = time.getUTCDate();
        const h = time.getUTCHours();
        const m = time.getUTCMinutes();
        const s = time.getUTCSeconds();
        return (
            fnZeroPadding(Y, 4) +
            "/" +
            fnZeroPadding(M, 2) +
            "/" +
            fnZeroPadding(D, 2) +
            " " +
            fnZeroPadding(h, 2) +
            ":" +
            fnZeroPadding(m, 2) +
            ":" +
            fnZeroPadding(s, 2) +
            " (UTC)"
        );
    };

    // Presenterに渡す
    return (
        <Presenter
            {...props}
            colors={colors}
            defaultValue={defaultValue}
            fnAddClickEvent={fnAddClickEvent}
            fnOnClickAddTimeline={fnOnClickAddTimeline}
            fnOnClickDelete={fnOnClickDelete}
            fnOnClickSave={fnOnClickSave}
            isLoading={isLoading}
            isOpenModalNoteDelete={isOpenModalNoteDelete}
            modules={modules}
            refButtonAddTimeline={refButtonAddTimeline}
            refQuill={refQuill}
            setIsOpenModalNoteDelete={setIsOpenModalNoteDelete}
            textNavigation={textNavigation}
            updatetime={updatetime}
        />
    );
};
